import { Box } from '@chakra-ui/react'
import type { FC } from 'react'
import { useState } from 'react'
import { FiPlay } from 'react-icons/fi'
import { useParams } from 'react-router-dom'

import MetricSourceRunResults from './metricSourceRunResults'

import type { ButtonProps } from '@app/components/ui/button'
import { Button } from '@app/components/ui/button'
import { METRIC_SOURCES } from '@app/lib/globals'
import ClearDataPointsPopover from '@app/pages/metrics/components/clearDataPointsPopover'
import useRunMetricSource from '@app/pages/metrics/components/metricSource/useRunMetricSource'
import type { MetricSource } from '@graphql/queries'

type Props = ButtonProps & {
  metricSource: Pick<MetricSource, 'id' | 'sourceName'> | null
}

const MetricSourceRunButton: FC<Props> = ({ metricSource, ...rest }) => {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [status, setStatus] = useState('failed')
  const [open, setOpen] = useState(false)
  const runMetricSource = useRunMetricSource()
  const { nodeId: metricId } = useParams()

  if (!metricSource || !METRIC_SOURCES[metricSource.sourceName].runnable) {
    return null
  }

  const runSource = async (clearDataPoints: boolean) => {
    setLoading(true)

    try {
      const { data } = await runMetricSource({
        metricId,
        metricSourceId: metricSource.id,
        clearDataPoints
      })

      setSuccess(data?.metricSourceRun?.success || false)
      setStatus(data?.metricSourceRun?.status || 'failed')
    } finally {
      setLoading(false)
      setOpen(true)
    }
  }

  const run = async () => {
    await runSource(false)
  }

  const clearAndRun = async () => {
    await runSource(true)
  }

  return (
    <Box>
      <MetricSourceRunResults open={open} onOpenChange={() => setOpen(false)} success={success} status={status} />
      <ClearDataPointsPopover onYes={clearAndRun} onNo={run}>
        <Button aria-label="Run metric source" loading={loading} {...rest}>
          <FiPlay />
          Run now
        </Button>
      </ClearDataPointsPopover>
    </Box>
  )
}

export default MetricSourceRunButton
